import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { ICreateSubRouteActivitie } from "./interfaces/create-sub-route-activitie.interface";
import { IUpdateSubRouteActivitie } from "./interfaces/update-sub-route-activitie.interface";
import SubRouteActivityAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route-activity/sub-route-activity.entity";
import { IFilters } from "../../filters/interfaces/filterInterface";
const apiUrl = env.API_ROUTING_URL;
const moduleName = "sub-route-activity";

@Injectable({
  providedIn: "root",
})
export class SubRouteActivityService extends AbstractService<
  SubRouteActivityAttributes,
  ICreateSubRouteActivitie,
  IUpdateSubRouteActivitie
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene actividades de ruta con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: code, name
   * Array filters: status
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<SubRouteActivityAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<SubRouteActivityAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<SubRouteActivityAttributes>[] => {
      const deletedFilter: FilterFn<SubRouteActivityAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof SubRouteActivityAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<SubRouteActivityAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<SubRouteActivityAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<SubRouteActivityAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<SubRouteActivityAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<SubRouteActivityAttributes[]>> {
    const query = buildQuery<SubRouteActivityAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteActivityAttributes[]>>(API_URL);
  }

}
